import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  ArrowRight,
  Lock,
  ShieldCheck,
  Zap,
  Play,
  Calendar,
  Clock,
} from "lucide-react";
import {
  parseSlot,
  formatSlot,
  TYPE_META,
  lifecycleState,
} from "@user/components/UpcomingConsultations";
import { photoSrc } from "@shared/photoSrc";

function durationText(consult) {
  return `${consult.duration_minutes || 15} minutes`;
}

function dateParts(consult) {
  const full = formatSlot(consult);
  const parts = full.split(",");
  return {
    day: parts.slice(0, 2).join(",").trim(),
    time: parts.slice(2).join(",").trim(),
  };
}

export function HeroAuth({ query, setQuery, onAsk, upcoming, loading }) {
  const hasUpcoming = !!upcoming;

  return (
    <section className="relative overflow-hidden adv-navy lg:rounded-3xl lg:mx-6 xl:mx-8 lg:mt-6">
      <div
        className="adv-blob bg-[#D4AF37]"
        style={{ width: 420, height: 420, top: -160, right: -120, opacity: 0.18 }}
      />
      <div
        className="adv-blob bg-[#1E40AF]"
        style={{ width: 360, height: 360, bottom: -160, left: -100, opacity: 0.3 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 relative">
        <div
          className={`grid gap-8 items-center ${
            hasUpcoming ? "lg:grid-cols-12" : "lg:grid-cols-10"
          }`}
        >
          <div
            className={
              hasUpcoming
                ? "lg:col-span-7"
                : "lg:col-span-10 xl:col-span-8"
            }
          >
            <span className="adv-chip">
              <ShieldCheck size={14} /> Your Legal Support
            </span>
            <h1
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
              style={{ fontFamily: "Poppins" }}
            >
              Get expert <span className="adv-gold-text">legal advice</span>,
              <br /> whenever you need it.
            </h1>
            <p className="mt-4 text-base lg:text-lg text-white/80 max-w-xl leading-relaxed">
              Describe your issue below to get instant guidance or connect with a verified lawyer.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-2 max-w-2xl bg-white rounded-2xl p-2 shadow-xl shadow-black/10">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search size={16} className="text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onAsk()}
                  placeholder="Describe your legal issue (rental dispute, FIR, divorce, GST...)"
                  className="border-0 shadow-none focus-visible:ring-0 text-[#0A2540] placeholder:text-slate-400"
                />
              </div>
              <Button
                onClick={onAsk}
                className="rounded-xl adv-btn-gold font-bold h-10 px-5"
              >
                Get Guidance <ArrowRight size={16} />
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5">
                <Lock size={14} className="text-[#D4AF37]" /> 100% private &amp; encrypted
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-emerald-400" /> Verified lawyers only
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Zap size={14} className="text-[#D4AF37]" /> Quick and easy
              </span>
            </div>
          </div>

          {hasUpcoming && (
            <div className="lg:col-span-5">
              <UpcomingCard consult={upcoming} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function UpcomingCard({ consult }) {
  const state = lifecycleState(consult);
  const meta = TYPE_META[consult.consultation_type] || TYPE_META.chat;
  const Icon = meta.icon;
  const { day, time } = dateParts(consult);
  const isLive = state === "live";

  return (
    <div className="bg-white rounded-2xl p-5 shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#0A2540]">
          <Calendar size={18} className="text-[#D4AF37]" />
          <h3 className="font-extrabold text-base">Upcoming Consultation</h3>
        </div>
        <Link
          to="/dashboard?tab=consults"
          className="text-xs font-bold text-[#1E3A8A] hover:underline inline-flex items-center gap-1"
        >
          View all <ArrowRight size={12} />
        </Link>
      </div>

      <div className="flex items-start gap-3">
        {consult.lawyer_photo ? (
          <img
            src={photoSrc(consult.lawyer_photo, "sm")}
            alt={consult.lawyer_name}
            className="h-14 w-14 rounded-full object-cover border border-slate-200"
          />
        ) : (
          <div className="h-14 w-14 rounded-full adv-navy text-[#D4AF37] grid place-items-center font-bold text-lg">
            {(consult.lawyer_name || "L")[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <span
            className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              isLive
                ? "bg-emerald-100 text-emerald-700"
                : "bg-emerald-50 text-emerald-700 border border-emerald-100"
            }`}
          >
            {isLive ? "Live" : (consult.status || "confirmed").toUpperCase()}
          </span>
          <p
            className="mt-1 font-bold text-[#0A2540] truncate"
            style={{ fontFamily: "Poppins" }}
          >
            {consult.lawyer_name}
          </p>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate">
            <span>{consult.lawyer_specialization || consult.specialization || "Legal consultation"}</span>
            <span className="text-slate-300">·</span>
            <Icon size={12} className={meta.color} />
            <span>{meta.label}</span>
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-slate-50 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1">
            <Calendar size={10} /> Date
          </p>
          <p className="mt-1 text-sm font-bold text-[#0A2540]">
            {day || "To be confirmed"}
          </p>
        </div>
        <div className="bg-slate-50 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1">
            <Clock size={10} /> Time
          </p>
          <p className="mt-1 text-sm font-bold text-[#0A2540]">{time || "--"}</p>
          <p className="text-[11px] text-slate-500">({durationText(consult)})</p>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        {isLive ? (
          <>
            <Button
              asChild
              className="flex-1 rounded-full adv-btn-primary font-bold h-10"
            >
              <Link to={`/consultations/${consult.id}`}>
                <Play size={14} fill="currentColor" /> Join Consultation
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="flex-1 rounded-full border-slate-200 font-semibold h-10"
            >
              <Link to={`/consultations/${consult.id}`}>View Details</Link>
            </Button>
          </>
        ) : (
          <Button
            asChild
            variant="outline"
            className="w-full rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold h-10"
          >
            <Link to={`/consultations/${consult.id}`}>View Details</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

export default HeroAuth;
